/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
} * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
/* eslint-disable react/prop-types */
import {Box} from '@mui/material';
import React, {useState} from 'react';
import {FormattedMessage} from 'react-intl';
import {intl} from '../../helper/IntlGlobalProvider';
import {withUser} from '../../UserProvider';
import {StreamSheetAppBar} from '../StreamSheetAppBar';
import StreamSheetNavigation from '../StreamSheetNavigation';
import StreamSheetPaper from '../utils/StreamSheetPaper';
import StreamSheetBreadCrumbs from '../utils/StreamSheetBreadCrumbs';
import {Database} from '../admin/Database';
import License from '../admin/License';
import {SystemInfo} from '../admin/SystemInfo';
import {UserInterface} from '../admin/UserInterface';
import {StreamSheetAccordion} from '../utils/StreamSheetAccordion';
import StreamSheetPageTitle from '../utils/StreamSheetPageTitle';
import { useNavigate } from 'react-router-dom';

const drawerWidth = 60;

export default withUser(({ user }) => ({
	showDatabase: user.rights.includes('database')
}))(function SettingsPage(props) {
	const [expanded, setExpanded] = useState(false);

	const handleChange = (panel) => {
		setExpanded( panel === expanded ? null : panel);
	};
	const navigate = useNavigate();

	document.title = intl.formatMessage({ id: 'TabTitle.Settings' }, {});

	return (
		<div
			style={{ width: 'inherit', height: 'inherit' }}
		>
			<div>
				<StreamSheetAppBar
					key="appPageBar"
					helpContext="settings"
				/>
				<StreamSheetNavigation openPage={navigate} />
			</div>
			<Box
				sx={{
					position: 'relative',
					height: 'calc(100% - 50px)',
					width: `calc(100% - ${drawerWidth}px)`,
					marginLeft: `${drawerWidth}px`,
					overflow: 'hidden',
					bgcolor: (theme) => theme.wall.backgroundColor
				}}
			>
				<StreamSheetBreadCrumbs path="Home/Settings" openPage={navigate} />
				<StreamSheetPaper
					data-tour="page-settings"
				>
					<div>
						<StreamSheetPageTitle
							title={<FormattedMessage id="SettingsPage.ApplicationSettings" defaultMessage="Streamsheet Settings" />}
							subTitle={<FormattedMessage id="SettingsPage.ApplicationSettingsSub" defaultMessage="Settings Sub" />}
						/>
						<Box sx={{height: 'calc(100% - 75px)', overflowY: 'auto'}}>
						<StreamSheetAccordion
							id="panelUI"
							expanded={expanded === 'panelUI'}
							onChange={() => handleChange('panelUI')}
							title={<FormattedMessage id="SettingsPage.User" defaultMessage="User Interface" />}
							subTitle={<FormattedMessage id="SettingsPage.UserSub" defaultMessage="User Interface Sub" />}
							details={<FormattedMessage id="SettingsPage.UserDetails" defaultMessage="User Interface Details" />}
						>
							<UserInterface />
						</StreamSheetAccordion>
						{props.showDatabase ?
						<StreamSheetAccordion
							id="panelDb"
							expanded={expanded === 'panelDb'}
							onChange={() => handleChange('panelDb')}
							title={<FormattedMessage id="SettingsPage.Database" defaultMessage="Database" />}
							subTitle={<FormattedMessage id="SettingsPage.DatabaseSub" defaultMessage="Database Sub" />}
							details={<FormattedMessage id="SettingsPage.DatabaseDetails" defaultMessage="Database Details" />}
						>
							<Database />
						</StreamSheetAccordion> : null}
						<StreamSheetAccordion
							id="panelLic"
							expanded={expanded === 'panelLic'}
							onChange={() => handleChange('panelLic')}
							title={<FormattedMessage id="SettingsPage.License" defaultMessage="License" />}
							subTitle={<FormattedMessage id="SettingsPage.LicenseSub" defaultMessage="License Sub" />}
							details={<FormattedMessage id="SettingsPage.LicenseDetails" defaultMessage="License Details" />}
						>
							<License />
						</StreamSheetAccordion>
						<StreamSheetAccordion
							id="panelSys"
							expanded={expanded === 'panelSys'}
							onChange={() => handleChange('panelSys')}
							title={<FormattedMessage id="SettingsPage.System" defaultMessage="System Info" />}
							subTitle={<FormattedMessage id="SettingsPage.SystemSub" defaultMessage="System Info Sub" />}
							details={<FormattedMessage id="SettingsPage.SystemDetails" defaultMessage="System Info Details" />}
						>
							<SystemInfo />
						</StreamSheetAccordion>
						</Box>
					</div>
				</StreamSheetPaper>
			</Box>
		</div>
	);
});

